import { useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  CONTRACTS__AUDIT,
  CONTRACTS__AUDIT_ACCURACY_RADIUS,
  CONTRACTS__AUDIT_CLOCK_IN,
  CONTRACTS__AUDIT_CLOCK_OUT,
  CONTRACTS__AUDIT_CONTRACT,
  CONTRACTS__AUDIT_CREATED,
  CONTRACTS__AUDIT_LAST_MODIFIED,
  CONTRACTS__AUDIT_ON_LOCATION,
  CONTRACTS__AUDIT_SERVICE,
  CONTRACTS__ON_LOCATION_KIOSK,
  CONTRACTS__ON_LOCATION_OFF,
  CONTRACTS__ON_LOCATION_ON,
  CONTRACTS__ON_LOCATION_UNKNOWN,
} from 'translations/constants';
import { ReactComponent as MapPinIcon } from '../../../assets/icons/MapPinIcon.svg';
import { OnLocationStatus } from '../types';
import {
  SectionCard,
  SectionTitle,
  SectionDivider,
  AuditRow,
  AuditFieldWrapper,
  AuditInlineField,
  ColumnTitleRow,
  StatusPill,
  StatusRow,
  AccuracyWrapper,
  AccuracyLabel,
  AccuracyText,
  SubSectionTitle,
  AuditValue,
} from '../styles';

interface AuditSectionProps {
  clockInOnLocationStatus: OnLocationStatus;
  clockInLocationAccuracyRadiusMeters: number | null;
  clockOutOnLocationStatus: OnLocationStatus;
  clockOutLocationAccuracyRadiusMeters: number | null;
  serverStartTime: string | null;
  serverEndTime: string | null;
  serviceDateCreatedDate: string | null;
  serviceDateCreatedUser: string | null;
  serviceDateLastModifiedDate: string | null;
  serviceDateLastModifiedUser: string | null;
  contractCreatedDate: string | null;
  contractCreatedUser: string | null;
  contractLastModifiedDate: string | null;
  contractLastModifiedUser: string | null;
}

const ON_LOCATION_TRANSLATION_MAP: Record<string, string> = {
  OnLocation: CONTRACTS__ON_LOCATION_ON,
  OffLocation: CONTRACTS__ON_LOCATION_OFF,
  Unknown: CONTRACTS__ON_LOCATION_UNKNOWN,
  Kiosk: CONTRACTS__ON_LOCATION_KIOSK,
};

const isEmptyDate = (iso: string | null): boolean => {
  if (!iso) return true;
  return new Date(iso).getFullYear() < 100;
};

const formatAuditTimestamp = (iso: string | null): string => {
  if (!iso) return '';
  const date = new Date(iso);
  return `${date.toLocaleDateString()} - ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const AuditSection = (props: AuditSectionProps) => {
  const {
    clockInOnLocationStatus,
    clockInLocationAccuracyRadiusMeters,
    clockOutOnLocationStatus,
    clockOutLocationAccuracyRadiusMeters,
    serverStartTime,
    serverEndTime,
    serviceDateCreatedDate,
    serviceDateCreatedUser,
    serviceDateLastModifiedDate,
    serviceDateLastModifiedUser,
    contractCreatedDate,
    contractCreatedUser,
    contractLastModifiedDate,
    contractLastModifiedUser,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();

  // Pill colors match the mobile app. Text colors and the grey background come from the
  // theme; the two light banner backgrounds have no theme key, so they use raw hex.
  const statusPillColors: Record<OnLocationStatus, { backgroundColor: string; color: string }> = {
    OnLocation: { backgroundColor: '#B8DBC7', color: theme.palette.primary.main },
    OffLocation: { backgroundColor: '#FAD2D9', color: theme.palette.error.main },
    Kiosk: { backgroundColor: theme.palette.info.main, color: theme.palette.secondary.dark },
    Unknown: { backgroundColor: theme.palette.info.main, color: theme.palette.secondary.dark },
  };

  // Location is now recorded per clock event. Each event is a horizontal row of
  // fields: the event time and the on-location status (a pill, with the accuracy radius
  // inline beside it — null for Unknown/Kiosk, shown as a plain "—"). A row is hidden
  // when it has neither a timestamp nor a meaningful status.
  const renderEventRow = (
    clockLabelKey: string,
    timestamp: string | null,
    status: OnLocationStatus,
    accuracyMeters: number | null,
  ) => {
    if (!timestamp && status === 'Unknown') return null;
    return (
      <AuditRow>
        {timestamp && (
          <AuditInlineField sx={{ flex: '1 1 0', minWidth: 0, justifyContent: 'space-between', paddingBottom: '7px' }}>
            <SubSectionTitle>{t(clockLabelKey)}</SubSectionTitle>
            <AuditValue>{formatAuditTimestamp(timestamp)}</AuditValue>
          </AuditInlineField>
        )}
        <AuditInlineField sx={{ flex: '2 1 0', minWidth: 0, paddingRight: '24px', gap: '12px' }}>
          <ColumnTitleRow>
            <MapPinIcon fill="#303C42" />
            <SubSectionTitle>{t(CONTRACTS__AUDIT_ON_LOCATION)}</SubSectionTitle>
          </ColumnTitleRow>
          <StatusRow>
            <StatusPill sx={statusPillColors[status] ?? statusPillColors.Unknown}>
              {t(ON_LOCATION_TRANSLATION_MAP[status] ?? CONTRACTS__ON_LOCATION_UNKNOWN)}
            </StatusPill>
            <AccuracyWrapper>
              <AccuracyLabel>{t(CONTRACTS__AUDIT_ACCURACY_RADIUS)}</AccuracyLabel>
              <AccuracyText>{accuracyMeters == null ? '—' : `${Math.round(accuracyMeters)} m`}</AccuracyText>
            </AccuracyWrapper>
          </StatusRow>
        </AuditInlineField>
      </AuditRow>
    );
  };

  const clockInRow = renderEventRow(
    CONTRACTS__AUDIT_CLOCK_IN,
    serverStartTime,
    clockInOnLocationStatus,
    clockInLocationAccuracyRadiusMeters,
  );
  const clockOutRow = renderEventRow(
    CONTRACTS__AUDIT_CLOCK_OUT,
    serverEndTime,
    clockOutOnLocationStatus,
    clockOutLocationAccuracyRadiusMeters,
  );

  const hasServiceDateCreated = !isEmptyDate(serviceDateCreatedDate);
  const hasServiceDateModified = !isEmptyDate(serviceDateLastModifiedDate);
  const hasServiceDateAudit = hasServiceDateCreated || hasServiceDateModified;
  const hasContractCreated = !isEmptyDate(contractCreatedDate);
  const hasContractModified = !isEmptyDate(contractLastModifiedDate);
  const hasContractAudit = hasContractCreated || hasContractModified;

  return (
    <SectionCard>
      <SectionTitle>{t(CONTRACTS__AUDIT)}</SectionTitle>
      <SectionDivider />
      {clockInRow}
      {clockOutRow}
      {hasServiceDateAudit && (
        <>
          <SubSectionTitle>{t(CONTRACTS__AUDIT_SERVICE)}</SubSectionTitle>
          <AuditRow>
            {hasServiceDateCreated && (
              <AuditFieldWrapper>
                <SubSectionTitle>{t(CONTRACTS__AUDIT_CREATED)}</SubSectionTitle>
                <AuditValue>{formatAuditTimestamp(serviceDateCreatedDate)}</AuditValue>
                {serviceDateCreatedUser && <AuditValue>{serviceDateCreatedUser}</AuditValue>}
              </AuditFieldWrapper>
            )}
            {hasServiceDateModified && (
              <AuditFieldWrapper>
                <SubSectionTitle>{t(CONTRACTS__AUDIT_LAST_MODIFIED)}</SubSectionTitle>
                <AuditValue>{formatAuditTimestamp(serviceDateLastModifiedDate)}</AuditValue>
                {serviceDateLastModifiedUser && <AuditValue>{serviceDateLastModifiedUser}</AuditValue>}
              </AuditFieldWrapper>
            )}
          </AuditRow>
        </>
      )}
      {hasContractAudit && (
        <>
          <SubSectionTitle>{t(CONTRACTS__AUDIT_CONTRACT)}</SubSectionTitle>
          <AuditRow>
            {hasContractCreated && (
              <AuditFieldWrapper>
                <SubSectionTitle>{t(CONTRACTS__AUDIT_CREATED)}</SubSectionTitle>
                <AuditValue>{formatAuditTimestamp(contractCreatedDate)}</AuditValue>
                {contractCreatedUser && <AuditValue>{contractCreatedUser}</AuditValue>}
              </AuditFieldWrapper>
            )}
            {hasContractModified && (
              <AuditFieldWrapper>
                <SubSectionTitle>{t(CONTRACTS__AUDIT_LAST_MODIFIED)}</SubSectionTitle>
                <AuditValue>{formatAuditTimestamp(contractLastModifiedDate)}</AuditValue>
                {contractLastModifiedUser && <AuditValue>{contractLastModifiedUser}</AuditValue>}
              </AuditFieldWrapper>
            )}
          </AuditRow>
        </>
      )}
    </SectionCard>
  );
};

export default AuditSection;
